import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';

const HOST_API = process.env.REACT_APP_URL;

const fmtFecha = (fechaStr) => {
    if (!fechaStr) return '';
    return new Date(fechaStr).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtFechaHora = (fechaStr) => {
    if (!fechaStr) return '';
    return new Date(fechaStr).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fotoSrc = (url) => `${HOST_API}${url}`.replace(/([^:])\/\/+/g, '$1/');

const ESTADO_COLORS = {
    'Pendiente':       { bg: '#fffaeb', color: '#b54708', border: '#fedf89' },
    'En proceso':      { bg: '#eff8ff', color: '#1570ef', border: '#b2ddff' },
    'Solucionado':     { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5' },
    'No solucionado':  { bg: '#fef3f2', color: '#f04438', border: '#fecdca' },
};

const estadoBadge = (estado, small) => {
    const s = ESTADO_COLORS[estado] || { bg: '#f2f4f7', color: '#667085', border: '#d0d5dd' };
    return (
        <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, fontSize: small ? '0.72rem' : '0.78rem', fontWeight: 700, padding: '0.1rem 0.55rem', borderRadius: 99 }}>
            {estado}
        </span>
    );
};

const CERRADOS = ['Solucionado', 'No solucionado'];

const initialForm = { tipoReclamoId: null, fecha: new Date(), descripcion: '' };

const Reclamos = () => {
    const toast      = useRef(null);
    const fileInputRef = useRef(null);
    const { barrio } = useContext(BarrioContext);
    const { administracionId } = useAuth();

    const user      = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
    const usuarioId   = user.id || user.usuarioId || 0;
    const solicitante = [user.nombre, user.apellido].filter(Boolean).join(' ') || 'Usuario';

    const [reclamos,   setReclamos]   = useState([]);
    const [tipos,      setTipos]      = useState([]);
    const [loading,    setLoading]    = useState(false);
    const [visible,    setVisible]    = useState(false);
    const [saving,     setSaving]     = useState(false);
    const [formData,   setFormData]   = useState(initialForm);
    const [archivo,    setArchivo]    = useState(null);

    // ─── Detalle / trazabilidad ────────────────────────────────────────────────────
    const [detalleVisible,     setDetalleVisible]     = useState(false);
    const [reclamoDetalle,     setReclamoDetalle]     = useState(null);
    const [seguimientos,       setSeguimientos]       = useState([]);
    const [loadingSeguimiento, setLoadingSeguimiento] = useState(false);

    const hc = (campo, valor) => setFormData(prev => ({ ...prev, [campo]: valor }));

    const fetchReclamos = useCallback(async () => {
        if (!barrio?.id || !usuarioId) return;
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${HOST_API}api/reclamos/GetReclamosUsuario?administracionId=${administracionId}&barrioId=${barrio.id}&usuarioId=${usuarioId}`
            );
            setReclamos(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los reclamos', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, usuarioId, administracionId]);

    const fetchTipos = useCallback(async () => {
        try {
            const { data } = await axios.get(`${HOST_API}api/tiposReclamo/GetTiposReclamo?administracionId=${administracionId}`);
            setTipos(data);
        } catch { /* silencioso */ }
    }, [administracionId]);

    useEffect(() => { fetchReclamos(); fetchTipos(); }, [fetchReclamos, fetchTipos]);

    const setFile = (file) => {
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.current.show({ severity: 'warn', summary: 'Archivo muy grande', detail: 'La foto no puede superar 10 MB', life: 3500 });
            return;
        }
        setArchivo(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.tipoReclamoId) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Seleccioná el tipo de reclamo', life: 3000 });
            return;
        }
        if (!formData.fecha) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Seleccioná la fecha', life: 3000 });
            return;
        }
        const d = formData.fecha;
        const fechaStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

        setSaving(true);
        try {
            const fd = new FormData();
            fd.append('model', JSON.stringify({
                administracionId,
                barrioId:         barrio.id,
                tipoReclamoId:    formData.tipoReclamoId,
                usuarioId,
                solicitante,
                descripcion:      formData.descripcion,
                fecha:            fechaStr,
            }));
            if (archivo) fd.append('file', archivo);

            await axios.post(`${HOST_API}api/reclamos/AddReclamo`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setVisible(false);
            setArchivo(null);
            fetchReclamos();
            toast.current.show({ severity: 'success', summary: 'Reclamo enviado', detail: 'Tu reclamo fue registrado correctamente', life: 3000 });
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo enviar el reclamo', life: 3000 });
        } finally {
            setSaving(false);
        }
    };

    // ─── Ver trazabilidad ───────────────────────────────────────────────────────
    const verTrazabilidad = async (r) => {
        setReclamoDetalle(r);
        setDetalleVisible(true);
        setLoadingSeguimiento(true);
        try {
            const { data } = await axios.get(`${HOST_API}api/reclamos/GetSeguimientos`, {
                params: { reclamoId: r.reclamoId, administracionId },
            });
            setSeguimientos(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar la trazabilidad', life: 3000 });
        } finally {
            setLoadingSeguimiento(false);
        }
    };

    const abiertos = reclamos.filter(r => !CERRADOS.includes(r.estado));
    const cerrados = reclamos.filter(r => CERRADOS.includes(r.estado));

    return (
        <>
            <Toast ref={toast} />

            {loading && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ProgressSpinner style={{ width: 52, height: 52 }} strokeWidth="4" />
                </div>
            )}

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                    <h4 style={{ margin: 0, color: '#101828' }}>Mis reclamos</h4>
                    <p style={{ margin: '0.3rem 0 0', color: '#667085', fontSize: '0.88rem' }}>
                        Enviás a nombre de <strong>{solicitante}</strong>
                    </p>
                </div>
                <Button label="Nuevo reclamo" icon="pi pi-plus" onClick={() => { setFormData(initialForm); setArchivo(null); setVisible(true); }} disabled={!barrio?.id} />
            </div>

            {/* Abiertos */}
            {abiertos.length > 0 && (
                <Section titulo="Abiertos" cantidad={abiertos.length} color="#dc6803">
                    {abiertos.map(r => <ReclamoCard key={r.reclamoId} reclamo={r} onVerTrazabilidad={() => verTrazabilidad(r)} />)}
                </Section>
            )}

            {/* Cerrados */}
            {cerrados.length > 0 && (
                <Section titulo="Cerrados" cantidad={cerrados.length} color="#667085" secundaria>
                    {cerrados.map(r => <ReclamoCard key={r.reclamoId} reclamo={r} onVerTrazabilidad={() => verTrazabilidad(r)} />)}
                </Section>
            )}

            {reclamos.length === 0 && !loading && (
                <div style={{ background: '#f9fafb', border: '1px dashed #e4e7ec', borderRadius: 12, padding: '2rem', textAlign: 'center', color: '#98a2b3' }}>
                    No tenés reclamos registrados
                </div>
            )}

            {/* Dialog nuevo reclamo */}
            <Dialog header="Nuevo reclamo" visible={visible} style={{ width: 460 }} breakpoints={{ '960px': '100vw' }} modal onHide={() => setVisible(false)}>
                <form onSubmit={handleSubmit}>
                    <div className="flex flex-column gap-3 mt-2">

                        <div>
                            <label style={labelStyle}>Tipo de reclamo <span style={req}>*</span></label>
                            <Dropdown
                                value={formData.tipoReclamoId}
                                options={tipos}
                                optionLabel="nombre"
                                optionValue="tipoReclamoId"
                                onChange={(e) => hc('tipoReclamoId', e.value)}
                                placeholder="Seleccioná el tipo"
                                className="p-inputtext-sm w-full"
                                filter
                                emptyMessage="No hay tipos configurados"
                            />
                        </div>

                        <div>
                            <label style={labelStyle}>Fecha <span style={req}>*</span></label>
                            <Calendar
                                value={formData.fecha}
                                onChange={(e) => hc('fecha', e.value)}
                                dateFormat="dd/mm/yy"
                                maxDate={new Date()}
                                showIcon
                                className="p-inputtext-sm w-full"
                            />
                        </div>

                        <div>
                            <label style={labelStyle}>Descripción <span style={{ color: '#98a2b3', fontWeight: 400 }}>(opcional)</span></label>
                            <InputTextarea
                                value={formData.descripcion}
                                onChange={(e) => hc('descripcion', e.target.value)}
                                rows={4}
                                autoResize
                                className="w-full"
                                placeholder="Describí el problema con el mayor detalle posible…"
                                maxLength={2000}
                            />
                            <span style={{ fontSize: '0.75rem', color: '#98a2b3' }}>{formData.descripcion.length}/2000</span>
                        </div>

                        <div>
                            <label style={labelStyle}>Foto <span style={{ color: '#98a2b3', fontWeight: 400 }}>(opcional)</span></label>
                            {!archivo ? (
                                <div
                                    onClick={() => fileInputRef.current?.click()}
                                    style={{
                                        border: '2px dashed #d0d5dd', borderRadius: 10, padding: '1rem',
                                        textAlign: 'center', cursor: 'pointer', background: '#fafafa',
                                    }}
                                >
                                    <i className="pi pi-camera" style={{ fontSize: '1.4rem', color: '#98a2b3', display: 'block', marginBottom: '0.3rem' }} />
                                    <span style={{ fontSize: '0.83rem', color: '#344054' }}>
                                        Arrastrá o <span style={{ color: '#1570ef' }}>seleccioná</span> una foto
                                    </span>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', border: '1px solid #d1fadf', borderRadius: 8, padding: '0.6rem 0.85rem', background: '#f6fef9' }}>
                                    <img src={URL.createObjectURL(archivo)} alt="preview" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6 }} />
                                    <span style={{ flex: 1, fontSize: '0.82rem', color: '#101828', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{archivo.name}</span>
                                    <button type="button" onClick={() => { setArchivo(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#98a2b3' }}>
                                        <i className="pi pi-times" />
                                    </button>
                                </div>
                            )}
                            <input ref={fileInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                                onChange={(e) => setFile(e.target.files[0])} />
                        </div>

                        <div className="flex justify-content-end gap-2 mt-1">
                            <Button label="Cancelar" type="button" severity="secondary" text onClick={() => setVisible(false)} />
                            <Button label={saving ? 'Enviando…' : 'Enviar reclamo'} icon={saving ? undefined : 'pi pi-send'} type="submit" disabled={saving} />
                        </div>
                    </div>
                </form>
            </Dialog>

            {/* Dialog trazabilidad */}
            <Dialog header={reclamoDetalle ? `Reclamo — ${reclamoDetalle.tipoReclamoNombre}` : ''} visible={detalleVisible} style={{ width: 480 }} breakpoints={{ '960px': '100vw' }} modal onHide={() => setDetalleVisible(false)}>
                {reclamoDetalle && (
                    <div>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem' }}>
                            {estadoBadge(reclamoDetalle.estado)}
                            <span style={{ fontSize: '0.8rem', color: '#98a2b3' }}>{fmtFecha(reclamoDetalle.fecha)}</span>
                        </div>
                        {reclamoDetalle.descripcion && <p style={{ margin: '0 0 0.75rem', fontSize: '0.87rem', color: '#344054' }}>{reclamoDetalle.descripcion}</p>}
                        {reclamoDetalle.fotoUrl && (
                            <a href={fotoSrc(reclamoDetalle.fotoUrl)} target="_blank" rel="noopener noreferrer">
                                <img src={fotoSrc(reclamoDetalle.fotoUrl)} alt="Foto del reclamo" style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 8, marginBottom: '1rem' }} />
                            </a>
                        )}

                        <h6 style={{ margin: '0 0 0.5rem', color: '#344054' }}>Trazabilidad</h6>
                        {loadingSeguimiento ? (
                            <div style={{ textAlign: 'center', padding: '1rem' }}><ProgressSpinner style={{ width: 32, height: 32 }} strokeWidth="4" /></div>
                        ) : (
                            <div>
                                {seguimientos.map(s => (
                                    <div key={s.reclamoSeguimientoId} style={{ borderLeft: '3px solid #d0d5dd', paddingLeft: '0.75rem', marginBottom: '0.75rem' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                            {estadoBadge(s.estado, true)}
                                            <span style={{ fontSize: '0.76rem', color: '#98a2b3' }}>{fmtFechaHora(s.fecha)}</span>
                                        </div>
                                        {s.texto && <p style={{ margin: '0.3rem 0 0', fontSize: '0.84rem', color: '#344054' }}>{s.texto}</p>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </Dialog>
        </>
    );
};

const Section = ({ titulo, cantidad, color, secundaria, children }) => (
    <div style={{ marginBottom: '1.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.85rem' }}>
            <h5 style={{ margin: 0, color: secundaria ? '#667085' : '#344054', fontWeight: 600 }}>{titulo}</h5>
            <span style={{ background: color + '1a', color, border: `1px solid ${color}33`, fontSize: '0.75rem', fontWeight: 700, padding: '0.1rem 0.55rem', borderRadius: 99 }}>
                {cantidad}
            </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>{children}</div>
    </div>
);

const ReclamoCard = ({ reclamo: r, onVerTrazabilidad }) => {
    const cerrado = CERRADOS.includes(r.estado);
    return (
        <div style={{
            background: cerrado ? '#fafafa' : '#fff',
            border: `1px solid ${cerrado ? '#e4e7ec' : '#fec84b'}`,
            borderLeft: `4px solid ${(ESTADO_COLORS[r.estado] || {}).color || '#dc6803'}`,
            borderRadius: 12,
            padding: '1rem 1.1rem',
            boxShadow: cerrado ? 'none' : '0 1px 3px rgba(0,0,0,0.04)',
            opacity: cerrado ? 0.85 : 1,
            cursor: 'pointer',
        }}
        onClick={onVerTrazabilidad}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.35rem' }}>
                <span style={{ fontWeight: 700, color: '#101828', fontSize: '0.95rem' }}>{r.tipoReclamoNombre}</span>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.78rem', color: '#667085' }}>{fmtFecha(r.fecha)}</span>
                    {estadoBadge(r.estado)}
                </div>
            </div>
            {r.descripcion && (
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#344054', whiteSpace: 'pre-wrap' }}>{r.descripcion}</p>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.5rem' }}>
                {r.fotoUrl
                    ? <span style={{ fontSize: '0.78rem', color: '#1570ef' }}><i className="pi pi-image" /> Con foto</span>
                    : <span />}
                <span style={{ fontSize: '0.78rem', color: '#1570ef', fontWeight: 600 }}>Ver trazabilidad <i className="pi pi-arrow-right" style={{ fontSize: '0.68rem' }} /></span>
            </div>
        </div>
    );
};

const labelStyle = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };
const req        = { color: '#f04438' };

export default Reclamos;
